import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Input, ButtonSiguiente, ButtonPopup } from "@/components/ui";
import { useProfesional } from "../context/ProfesionalContext";
import { LandingData } from "../core/buildLandingData";
import { safeFetch } from "@/api/safeFetch";
import Alerta from "@/components/alert/Alerta";

const EditarContactSection: FC = () => {
  const navigate = useNavigate();

  const { landingData, setLandingData, loading, perfilId } = useProfesional();
  const { profesionalId, profesionId } = useParams();
  const [sectionId, setSectionId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [alert, setAlert] = useState<{
    mensaje: string;
    type: "success" | "error" | "warning" | "info";
  } | null>(null);

  useEffect(() => {
    const loadSection = async () => {
      if (!perfilId) return;

      try {
        const res = await safeFetch(`/api/landing/${perfilId}`);
        const data = await res.json();

        const contact = data?.contact ?? {};

        setSectionId(contact.id ?? null);

        const props = contact.props ?? {};

        setLandingData((prev) => {
          if (!prev) return prev;

          return {
            ...prev,
            contact: {
              ...prev.contact,

              title: props.title ?? "",
              subtitle: props.subtitle ?? "",

              phone: props.phone ?? "",

              // Si existe uno personalizado úsalo,
              // si no, conserva el que ya viene del perfil.
              correo_perfil:
                props.correo_perfil ?? prev.contact.correo_perfil ?? "",

              whatsapp: props.whatsapp ?? "",
            },
          };
        });
      } catch (err) {
        console.error(err);
      }
    };

    loadSection();
  }, [perfilId, setLandingData]);

  if (loading) {
    return <div className="p-8 text-white">Cargando contacto...</div>;
  }

  if (!landingData?.contact) {
    return <div className="p-8 text-red-400">No se pudo cargar la landing</div>;
  }

  const contact = landingData.contact;

  const handleChange = (
    field: "title" | "subtitle" | "correo_perfil" | "phone" | "whatsapp",
    value: string,
  ) => {
    setLandingData((prev: LandingData | null) => {
      if (!prev) return prev;

      return {
        ...prev,
        contact: {
          ...prev.contact,
          [field]: value,
        },
      };
    });
  };

  const saveContact = async () => {
    try {
      setSaving(true);

      if (!sectionId) {
        setAlert({
          mensaje: "No existe sectionId",
          type: "error",
        });
        return;
      }

      const res = await safeFetch(`/api/landing/section/${sectionId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          props: {
            title: contact.title,
            subtitle: (contact as any).subtitle,
            phone: contact.phone,
            correo_perfil: contact.correo_perfil,
            whatsapp: contact.whatsapp,
          },
        }),
      });

      if (!res.ok) {
        throw new Error("Error guardando contacto");
      }

      await res.json();

      window.dispatchEvent(new Event("landingUpdated"));

      setAlert({
        mensaje: "✅ Contacto guardado correctamente",
        type: "success",
      });
    } catch (err) {
      console.error(err);

      setAlert({
        mensaje: "❌ Error al guardar contacto",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const card = `relative overflow-hidden rounded-lg border-r-4 border-t-2 border-cyan-300/50 bg-gradient-to-br from-white/10 via-white/5 to-transparent backdrop-blur-3xl shadow-md shadow-zinc-800 p-8`;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10 text-white">
      {/* VOLVER */}
      <div className="absolute top-4 left-4 z-50 pointer-events-auto">
        <ButtonSiguiente
          onClick={() =>
            navigate(
              `/editar-landing/${profesionalId}/${profesionId}/${perfilId}`,
              {
                state: {
                  showLandingPrincipal: true,
                },
              },
            )
          }
          className="
      px-4 py-2
      rounded-lg
      bg-zinc-900
      border border-white/10
      hover:border-cyan-500
      transition
    "
        >
          ← Volver
        </ButtonSiguiente>
      </div>

      {/* HEADER */}
      <div className="w-full rounded-lg border-t-2 border-b-2 border-cyan-300 bg-zinc-950 py-2 px-8 shadow-md shadow-zinc-800 backdrop-blur-xl mb-10 mt-5">
        <div className="flex flex-col gap-8 md:flex-row md:items-center md:justify-between">
          <div className="space-y-3 flex-1">
            <h1 className="text-3xl font-bold tracking-tight">
              Editor de Contacto
            </h1>

            <p className="text-zinc-400 text-sm max-w-md">
              Gestiona los datos de contacto visibles en tu landing profesional.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <ButtonPopup
              onClick={saveContact}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-zinc-900 border border-white/10 hover:border-cyan-500 transition flex items-center justify-center whitespace-nowrap overflow-hidden text-ellipsis max-w-full"
            >
              {saving ? "Guardando..." : "Guardar cambios"}
            </ButtonPopup>
          </div>
        </div>
      </div>

      {/* CONFIGURACIÓN */}
      <section className={`${card} text-white bg-zinc-900 `}>
        <h2 className="text-xl font-semibold mb-2">Información de contacto</h2>

        <p className="text-zinc-400 text-sm mb-8">
          Configura la información que aparecerá en la sección de contacto de tu
          landing.
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-zinc-300">Título</label>

            <Input
              value={contact.title}
              onChange={(e) => handleChange("title", e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-zinc-300">
              Subtítulo
            </label>

            <Input
              value={(contact as any).subtitle ?? ""}
              onChange={(e) => handleChange("subtitle", e.target.value)}
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-zinc-300">
              Teléfono
            </label>

            <Input
              value={contact.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-zinc-300">Email</label>

            <Input
              type="email"
              value={contact.correo_perfil ?? ""}
              onChange={(e) => handleChange("correo_perfil", e.target.value)}
            />
            
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-zinc-300">WhatsApp</label>

          <Input
            value={contact.whatsapp}
            onChange={(e) => handleChange("whatsapp", e.target.value)}
          />
        </div>
      </section>

      {/* ALERTA */}
      {alert && (
        <Alerta
          mensaje={alert.mensaje}
          type={alert.type}
          onClose={() => setAlert(null)}
        />
      )}
    </div>
  );
};

export default EditarContactSection;
